import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Tag, Button, Drawer, Form, Input, Select,
  Typography, Card, Row, Col, Statistic, Space,
  Modal, Tooltip, message as antMessage, Empty, Spin,
} from 'antd';
import {
  PlusOutlined, CustomerServiceOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SendOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { theme as antdTheme } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import axios from 'axios';
import { PRO_PRICE_MONTH } from '../utils/pricing';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = { open: 'blue', in_progress: 'orange', resolved: 'green', closed: 'default' };
const STATUS_LABEL = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
const PRIORITY_COLOR = { high: 'red', medium: 'orange', low: 'green' };
const CATEGORY_LABEL = { general: 'General', billing: 'Billing', technical: 'Technical', feature_request: 'Feature Request' };

const Support = () => {
  const { token: tk } = antdTheme.useToken();
  const [tickets, setTickets]         = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [statusF, setStatusF]         = useState('');

  // Drawer state
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [activeTicket, setActive]     = useState(null);
  const [messages, setMessages]       = useState([]);
  const [threadLoading, setTLoading]  = useState(false);
  const [replyText, setReplyText]     = useState('');
  const [replying, setReplying]       = useState(false);
  const threadEndRef = useRef(null);

  // New ticket modal
  const [newOpen, setNewOpen]         = useState(false);
  const [creating, setCreating]       = useState(false);
  const [form]                        = Form.useForm();
  const [upgradeModal, setUpgrade]    = useState(false);
  const [upgrading, setUpgrading]     = useState(false);

  // Plan from token
  const plan = (() => { try { return JSON.parse(atob(localStorage.getItem('token').split('.')[1])).plan; } catch { return 'free'; } })();

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await axios.post('/api/billing/checkout', {}, { headers: authHeader() });
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Upgrade error:', err);
      setUpgrading(false);
    }
  };

  const loadTickets = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (statusF) params.status = statusF;
      const res = await axios.get('/api/support', { headers: authHeader(), params });
      setTickets(res.data.data);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusF]);

  useEffect(() => { loadTickets(1); }, [loadTickets]);

  const openTicket = async (ticket) => {
    setActive(ticket);
    setDrawerOpen(true);
    setTLoading(true);
    try {
      const res = await axios.get(`/api/support/${ticket.id}`, { headers: authHeader() });
      setActive(res.data.ticket);
      setMessages(res.data.messages);
      // Mark as read in local state
      setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, user_unread: false } : t));
    } catch (err) { console.error(err); }
    finally { setTLoading(false); }
  };

  useEffect(() => {
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages when drawer is open
  useEffect(() => {
    if (!drawerOpen || !activeTicket) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`/api/support/${activeTicket.id}`, { headers: authHeader() });
        setMessages(res.data.messages);
        setActive(res.data.ticket);
      } catch (_) {}
    }, 30000);
    return () => clearInterval(id);
  }, [drawerOpen, activeTicket]);

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await axios.post(`/api/support/${activeTicket.id}/reply`, { message: replyText }, { headers: authHeader() });
      setReplyText('');
      const res = await axios.get(`/api/support/${activeTicket.id}`, { headers: authHeader() });
      setMessages(res.data.messages);
      setActive(res.data.ticket);
      loadTickets(page);
    } catch (err) {
      if (err.response?.data?.upgrade) setUpgrade(true);
      else antMessage.error(err.response?.data?.message || 'Failed to send reply');
    }
    setReplying(false);
  };

  const closeTicket = async () => {
    try {
      await axios.put(`/api/support/${activeTicket.id}/close`, {}, { headers: authHeader() });
      setActive(prev => ({ ...prev, status: 'closed' }));
      loadTickets(page);
      antMessage.success('Ticket closed');
    } catch (err) { antMessage.error('Failed to close ticket'); }
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      await axios.post('/api/support', values, { headers: authHeader() });
      antMessage.success('Ticket created! Our team will respond shortly.');
      setNewOpen(false);
      form.resetFields();
      loadTickets(1);
    } catch (err) {
      if (err.response?.data?.upgrade) setUpgrade(true);
      else antMessage.error(err.response?.data?.message || 'Failed to create ticket');
    }
    setCreating(false);
  };

  // Stats
  const open_count       = tickets.filter(t => t.status === 'open').length;
  const inprogress_count = tickets.filter(t => t.status === 'in_progress').length;
  const resolved_count   = tickets.filter(t => t.status === 'resolved').length;

  const columns = [
    {
      title: '', key: 'unread', width: 12,
      render: (_, r) => r.user_unread ? <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-primary)', margin: '0 auto' }} /> : null,
    },
    {
      title: 'Subject', key: 'subject',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Text strong style={{ fontSize: 13 }}>{r.subject}</Text>
          <Space size={6}>
            <Tag style={{ fontSize: 11, margin: 0 }}>{CATEGORY_LABEL[r.category] || r.category}</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>#{r.id}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Priority', dataIndex: 'priority', width: 90,
      render: p => <Tag color={PRIORITY_COLOR[p] || 'default'} style={{ textTransform: 'capitalize' }}>{p}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      render: s => <Tag color={STATUS_COLOR[s] || 'default'}>{STATUS_LABEL[s] || s}</Tag>,
    },
    {
      title: 'Last Update', dataIndex: 'last_reply_at', width: 130,
      render: (d, r) => <Text type="secondary" style={{ fontSize: 12 }}>{d ? dayjs(d).fromNow() : dayjs(r.created_at).fromNow()}</Text>,
    },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>Support</Title>
          <Text type="secondary">Create and track support requests.</Text>
        </div>
        <Button
          type="primary" icon={<PlusOutlined />}
          onClick={() => plan === 'pro' ? setNewOpen(true) : setUpgrade(true)}
          style={{ borderRadius: 10, fontWeight: 600 }}
        >
          New Ticket
        </Button>
      </div>

      {/* Stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {[
          { label: 'Open',        value: open_count,       color: '#3b82f6', icon: <CustomerServiceOutlined /> },
          { label: 'In Progress', value: inprogress_count, color: '#f59e0b', icon: <ClockCircleOutlined /> },
          { label: 'Resolved',    value: resolved_count,   color: '#10b981', icon: <CheckCircleOutlined /> },
          { label: 'Total',       value: total,            color: tk.colorTextSecondary, icon: <CustomerServiceOutlined /> },
        ].map(s => (
          <Col xs={12} sm={6} key={s.label}>
            <Card size="small" style={{ borderRadius: 10, border: `1px solid ${tk.colorBorderSecondary}` }}>
              <Statistic
                title={<Text type="secondary" style={{ fontSize: 12 }}>{s.label}</Text>}
                value={s.value}
                valueStyle={{ color: s.color, fontSize: 22, fontWeight: 700 }}
                prefix={React.cloneElement(s.icon, { style: { fontSize: 14, color: s.color, marginRight: 4 } })}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }} styles={{ body: { padding: 0 } }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${tk.colorBorderSecondary}` }}>
          <Select value={statusF || 'all'} onChange={v => setStatusF(v === 'all' ? '' : v)} style={{ width: 150 }}>
            <Option value="all">All Tickets</Option>
            <Option value="open">Open</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="resolved">Resolved</Option>
            <Option value="closed">Closed</Option>
          </Select>
        </div>

        {total === 0 && !loading ? (
          <div style={{ padding: '60px 24px', textAlign: 'center' }}>
            <CustomerServiceOutlined style={{ fontSize: 40, color: tk.colorTextQuaternary, display: 'block', marginBottom: 12 }} />
            <Text type="secondary" style={{ fontSize: 14 }}>No support tickets yet.</Text>
            <br />
            <Button type="primary" style={{ marginTop: 16, borderRadius: 8 }} onClick={() => plan === 'pro' ? setNewOpen(true) : setUpgrade(true)}>
              Create your first ticket
            </Button>
          </div>
        ) : (
          <Table
            rowKey="id" dataSource={tickets} columns={columns} loading={loading} size="small"
            onRow={r => ({ onClick: () => openTicket(r), style: { cursor: 'pointer' } })}
            rowClassName={r => r.user_unread ? 'ant-table-row-selected' : ''}
            pagination={{ current: page, pageSize: 20, total, onChange: p => loadTickets(p), showTotal: n => `${n} tickets`, style: { padding: '12px 16px', margin: 0 } }}
            style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
          />
        )}
      </Card>

      {/* Ticket thread drawer */}
      <Drawer
        title={activeTicket ? (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 15 }}>#{activeTicket.id} — {activeTicket.subject}</Text>
              <Space>
                <Tag color={PRIORITY_COLOR[activeTicket.priority]}>{activeTicket.priority}</Tag>
                <Tag color={STATUS_COLOR[activeTicket.status]}>{STATUS_LABEL[activeTicket.status]}</Tag>
              </Space>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {CATEGORY_LABEL[activeTicket.category]} · Opened {dayjs(activeTicket.created_at).fromNow()}
            </Text>
          </Space>
        ) : 'Support Ticket'}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setMessages([]); setReplyText(''); }}
        width={580}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
        extra={
          activeTicket?.status !== 'closed' && (
            <Button size="small" danger onClick={closeTicket} icon={<CloseCircleOutlined />}>Close ticket</Button>
          )
        }
      >
        {threadLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin /></div>
        ) : (
          <>
            {/* Message thread */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {messages.map(m => {
                const isUser = m.sender_role === 'user';
                return (
                  <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: 11, marginBottom: 4 }}>
                      {isUser ? 'You' : '🛡️ Support Team'} · {dayjs(m.created_at).fromNow()}
                    </Text>
                    <div style={{
                      maxWidth: '80%', padding: '10px 14px', borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      background: isUser ? 'var(--brand-primary)' : tk.colorFillAlter,
                      color: isUser ? '#fff' : tk.colorText,
                      fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {m.message}
                    </div>
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>

            {/* Reply box */}
            {activeTicket?.status !== 'closed' ? (
              <div style={{ padding: '16px 24px', borderTop: `1px solid ${tk.colorBorderSecondary}`, background: tk.colorBgContainer }}>
                <TextArea
                  rows={3}
                  placeholder="Type your reply…"
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) sendReply(); }}
                  style={{ borderRadius: 8, marginBottom: 8, resize: 'none' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button type="primary" icon={<SendOutlined />} loading={replying} onClick={sendReply} style={{ borderRadius: 8 }}>
                    Send Reply
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '16px 24px', textAlign: 'center', borderTop: `1px solid ${tk.colorBorderSecondary}` }}>
                <Text type="secondary">This ticket is closed.</Text>
              </div>
            )}
          </>
        )}
      </Drawer>

      {/* New Ticket Modal */}
      <Modal title={<Space><CustomerServiceOutlined />New Support Ticket</Space>}
        open={newOpen} onCancel={() => { setNewOpen(false); form.resetFields(); }}
        footer={null} width={520} destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
            <Input placeholder="Brief description of your issue" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="category" label="Category" initialValue="general">
                <Select>
                  <Option value="general">General</Option>
                  <Option value="billing">Billing</Option>
                  <Option value="technical">Technical</Option>
                  <Option value="feature_request">Feature Request</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" initialValue="medium">
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="message" label="Message" rules={[{ required: true }]}>
            <TextArea rows={5} placeholder="Describe your issue in detail…" style={{ borderRadius: 8, resize: 'none' }} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setNewOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creating} style={{ borderRadius: 8 }}>Submit Ticket</Button>
          </div>
        </Form>
      </Modal>

      {/* Upgrade Modal */}
      <Modal open={upgradeModal} onCancel={() => setUpgrade(false)} footer={null} centered width={420}>
        <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#f59e0b,#ef4444)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <ThunderboltOutlined style={{ fontSize: 28, color: '#fff' }} />
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Pro plan required</h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>Support tickets are available on the Pro plan. Upgrade to get priority support and direct access to our team.</p>
          <Button type="primary" size="large" icon={<ThunderboltOutlined />} block loading={upgrading} onClick={handleUpgrade} style={{ borderRadius: 10, fontWeight: 600, height: 46 }}>
            Upgrade to Pro — {PRO_PRICE_MONTH}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Support;
